class EZProductSection {
  constructor(root) {
    if (!root || root.dataset.ezInitialized === 'true') return;

    const variantsScript = root.querySelector('[data-ez-variants]');
    const form = root.querySelector('form[action*="/cart/add"]');

    if (!variantsScript || !form) {
      console.warn('EZ Product Section: product data or product form was not found.', root);
      return;
    }

    root.dataset.ezInitialized = 'true';
    this.root = root;
    this.form = form;
    this.idInput = form.querySelector('[name="id"]');
    this.qtyInput = form.querySelector('[name="quantity"]');
    this.price = root.querySelector('[data-ez-price]');
    this.button = root.querySelector('[data-ez-add]');
    this.selectedOptions = {};

    const selectedBundle = root.querySelector('[data-ez-bundle].is-selected');
    this.selectedButtonText =
      selectedBundle?.dataset.buttonText ||
      this.button?.dataset.availableText ||
      'ADD TO CART';

    try {
      this.variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      console.error('EZ Product Section: unable to parse variant data.', error);
      return;
    }

    root.querySelectorAll('[data-option-position]').forEach(group => {
      const active = group.querySelector('[data-option-value].is-active');
      if (active) {
        this.selectedOptions[group.dataset.optionPosition] = active.dataset.optionValue;
      }
    });

    this.bindGallery();
    this.bindBundles();
    this.bindVariants();
    this.updateVariant();
  }

  bindGallery() {
    const main = this.root.querySelector('[data-ez-main-image]');
    const mainWrap = main?.closest('.ez-gallery__main');

    if (!main || !mainWrap) return;

    this.galleryMain = main;
    this.galleryMainWrap = mainWrap;
    this.galleryZoom = 1;
    this.galleryZoomMin = 1;
    this.galleryZoomMax = 3;
    this.galleryZoomStep = 0.5;

    main.classList.add('ez-gallery__zoom-image');
    main.draggable = false;

    this.setGalleryZoom = (nextZoom, originX = 50, originY = 50) => {
      const zoom = Math.max(
        this.galleryZoomMin,
        Math.min(this.galleryZoomMax, Number(nextZoom))
      );

      this.galleryZoom = zoom;
      main.style.setProperty('--ez-zoom', zoom);
      main.style.transformOrigin = `${originX}% ${originY}%`;
      mainWrap.classList.toggle('is-zoomed', zoom > 1);

      const zoomOut = mainWrap.querySelector('[data-ez-zoom-out]');
      const zoomIn = mainWrap.querySelector('[data-ez-zoom-in]');

      if (zoomOut) zoomOut.disabled = zoom <= this.galleryZoomMin;
      if (zoomIn) zoomIn.disabled = zoom >= this.galleryZoomMax;
    };

    this.resetGalleryZoom = () => {
      this.setGalleryZoom(1, 50, 50);
    };

    if (!mainWrap.querySelector('[data-ez-gallery-controls]')) {
      const controls = document.createElement('div');
      controls.className = 'ez-gallery__controls';
      controls.setAttribute('data-ez-gallery-controls', '');
      controls.innerHTML = `
        <button type="button" class="ez-gallery__control" data-ez-zoom-out aria-label="Zoom out" title="Zoom out">
          <span aria-hidden="true">−</span>
        </button>
        <button type="button" class="ez-gallery__control" data-ez-zoom-in aria-label="Zoom in" title="Zoom in">
          <span aria-hidden="true">+</span>
        </button>
        <button type="button" class="ez-gallery__control ez-gallery__control--preview" data-ez-fullscreen aria-label="Open full image preview" title="Full preview">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 3H3v5M16 3h5v5M21 16v5h-5M8 21H3v-5"></path>
          </svg>
        </button>
      `;
      mainWrap.appendChild(controls);
    }

    const zoomOutButton = mainWrap.querySelector('[data-ez-zoom-out]');
    const zoomInButton = mainWrap.querySelector('[data-ez-zoom-in]');
    const fullscreenButton = mainWrap.querySelector('[data-ez-fullscreen]');

    zoomOutButton?.addEventListener('click', event => {
      event.stopPropagation();
      this.setGalleryZoom(this.galleryZoom - this.galleryZoomStep);
    });

    zoomInButton?.addEventListener('click', event => {
      event.stopPropagation();
      this.setGalleryZoom(this.galleryZoom + this.galleryZoomStep);
    });

    main.addEventListener('click', event => {
      if (window.matchMedia('(pointer: coarse)').matches) return;

      const rect = mainWrap.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      if (this.galleryZoom === 1) {
        this.setGalleryZoom(2, x, y);
      } else {
        this.resetGalleryZoom();
      }
    });

    mainWrap.addEventListener('mousemove', event => {
      if (this.galleryZoom <= 1) return;

      const rect = mainWrap.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      main.style.transformOrigin = `${Math.max(0, Math.min(100, x))}% ${Math.max(
        0,
        Math.min(100, y)
      )}%`;
    });

    mainWrap.addEventListener(
      'wheel',
      event => {
        if (!event.ctrlKey && this.galleryZoom === 1) return;

        event.preventDefault();

        const rect = mainWrap.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        const direction = event.deltaY < 0 ? 1 : -1;

        this.setGalleryZoom(
          this.galleryZoom + direction * this.galleryZoomStep,
          x,
          y
        );
      },
      { passive: false }
    );

    this.root.querySelectorAll('[data-ez-thumb]').forEach(button => {
      button.addEventListener('click', () => {
        this.root
          .querySelectorAll('[data-ez-thumb]')
          .forEach(item => item.classList.remove('is-active'));

        button.classList.add('is-active');

        if (button.dataset.image) {
          main.src = button.dataset.image;
          main.removeAttribute('srcset');
          this.resetGalleryZoom();
        }
      });
    });

    this.buildLightbox();

    fullscreenButton?.addEventListener('click', event => {
      event.stopPropagation();
      this.openLightbox(main.currentSrc || main.src);
    });

    this.setGalleryZoom(1);
  }

  getGalleryImages() {
    const images = [];

    this.root.querySelectorAll('[data-ez-thumb]').forEach(button => {
      if (!button.dataset.image) return;

      const thumbImage = button.querySelector('img');

      if (!images.some(item => item.src === button.dataset.image)) {
        images.push({
          src: button.dataset.image,
          alt: thumbImage?.alt || this.galleryMain?.alt || 'Product image'
        });
      }
    });

    const currentSrc = this.galleryMain?.currentSrc || this.galleryMain?.src;

    if (currentSrc && !images.some(item => item.src === currentSrc)) {
      images.unshift({
        src: currentSrc,
        alt: this.galleryMain?.alt || 'Product image'
      });
    }

    return images;
  }

  buildLightbox() {
    if (this.lightbox) return;

    const lightbox = document.createElement('div');
    lightbox.className = 'ez-lightbox';
    lightbox.setAttribute('data-ez-lightbox', '');
    lightbox.setAttribute('aria-hidden', 'true');

    lightbox.innerHTML = `
      <div class="ez-lightbox__backdrop" data-ez-lightbox-close></div>

      <div class="ez-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Product image preview">
        <div class="ez-lightbox__toolbar">
          <div class="ez-lightbox__zoom-controls">
            <button type="button" class="ez-lightbox__button" data-ez-lightbox-zoom-out aria-label="Zoom out" title="Zoom out">
              <span aria-hidden="true">−</span>
            </button>

            <button type="button" class="ez-lightbox__button" data-ez-lightbox-zoom-reset aria-label="Reset zoom" title="Reset zoom">
              <span data-ez-lightbox-zoom-label>100%</span>
            </button>

            <button type="button" class="ez-lightbox__button" data-ez-lightbox-zoom-in aria-label="Zoom in" title="Zoom in">
              <span aria-hidden="true">+</span>
            </button>
          </div>

          <button type="button" class="ez-lightbox__button ez-lightbox__close" data-ez-lightbox-close aria-label="Close image preview" title="Close">
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <button type="button" class="ez-lightbox__nav ez-lightbox__nav--prev" data-ez-lightbox-prev aria-label="Previous image">
          <span aria-hidden="true">‹</span>
        </button>

        <div class="ez-lightbox__stage" data-ez-lightbox-stage>
          <img class="ez-lightbox__image" data-ez-lightbox-image alt="">
        </div>

        <button type="button" class="ez-lightbox__nav ez-lightbox__nav--next" data-ez-lightbox-next aria-label="Next image">
          <span aria-hidden="true">›</span>
        </button>

        <div class="ez-lightbox__thumbs" data-ez-lightbox-thumbs></div>
      </div>
    `;

    document.body.appendChild(lightbox);

    this.lightbox = lightbox;
    this.lightboxImage = lightbox.querySelector('[data-ez-lightbox-image]');
    this.lightboxStage = lightbox.querySelector('[data-ez-lightbox-stage]');
    this.lightboxThumbs = lightbox.querySelector('[data-ez-lightbox-thumbs]');
    this.lightboxZoomLabel = lightbox.querySelector('[data-ez-lightbox-zoom-label]');
    this.lightboxIndex = 0;
    this.lightboxZoom = 1;
    this.lightboxMinZoom = 1;
    this.lightboxMaxZoom = 4;
    this.lightboxZoomStep = 0.5;

    this.setLightboxZoom = (nextZoom, originX = 50, originY = 50) => {
      const zoom = Math.max(
        this.lightboxMinZoom,
        Math.min(this.lightboxMaxZoom, Number(nextZoom))
      );

      this.lightboxZoom = zoom;
      this.lightboxImage.style.setProperty('--ez-lightbox-zoom', zoom);
      this.lightboxImage.style.transformOrigin = `${originX}% ${originY}%`;
      this.lightboxStage.classList.toggle('is-zoomed', zoom > 1);

      if (this.lightboxZoomLabel) {
        this.lightboxZoomLabel.textContent = `${Math.round(zoom * 100)}%`;
      }

      const zoomOut = lightbox.querySelector('[data-ez-lightbox-zoom-out]');
      const zoomIn = lightbox.querySelector('[data-ez-lightbox-zoom-in]');

      if (zoomOut) zoomOut.disabled = zoom <= this.lightboxMinZoom;
      if (zoomIn) zoomIn.disabled = zoom >= this.lightboxMaxZoom;
    };

    this.renderLightboxImage = () => {
      const item = this.lightboxImages?.[this.lightboxIndex];
      if (!item) return;

      this.lightboxImage.src = item.src;
      this.lightboxImage.alt = item.alt || 'Product image';
      this.setLightboxZoom(1);

      this.lightboxThumbs
        ?.querySelectorAll('[data-ez-lightbox-thumb]')
        .forEach((thumb, index) => {
          thumb.classList.toggle('is-active', index === this.lightboxIndex);
          thumb.setAttribute(
            'aria-current',
            index === this.lightboxIndex ? 'true' : 'false'
          );
        });

      const hasMultiple = (this.lightboxImages?.length || 0) > 1;

      const prev = lightbox.querySelector('[data-ez-lightbox-prev]');
      const next = lightbox.querySelector('[data-ez-lightbox-next]');

      if (prev) prev.hidden = !hasMultiple;
      if (next) next.hidden = !hasMultiple;
    };

    this.showPreviousLightboxImage = () => {
      if (!this.lightboxImages?.length) return;

      this.lightboxIndex =
        (this.lightboxIndex - 1 + this.lightboxImages.length) %
        this.lightboxImages.length;

      this.renderLightboxImage();
    };

    this.showNextLightboxImage = () => {
      if (!this.lightboxImages?.length) return;

      this.lightboxIndex =
        (this.lightboxIndex + 1) % this.lightboxImages.length;

      this.renderLightboxImage();
    };

    lightbox.querySelectorAll('[data-ez-lightbox-close]').forEach(button => {
      button.addEventListener('click', () => this.closeLightbox());
    });

    lightbox
      .querySelector('[data-ez-lightbox-prev]')
      ?.addEventListener('click', () => this.showPreviousLightboxImage());

    lightbox
      .querySelector('[data-ez-lightbox-next]')
      ?.addEventListener('click', () => this.showNextLightboxImage());

    lightbox
      .querySelector('[data-ez-lightbox-zoom-out]')
      ?.addEventListener('click', () =>
        this.setLightboxZoom(this.lightboxZoom - this.lightboxZoomStep)
      );

    lightbox
      .querySelector('[data-ez-lightbox-zoom-in]')
      ?.addEventListener('click', () =>
        this.setLightboxZoom(this.lightboxZoom + this.lightboxZoomStep)
      );

    lightbox
      .querySelector('[data-ez-lightbox-zoom-reset]')
      ?.addEventListener('click', () => this.setLightboxZoom(1));

    this.lightboxStage?.addEventListener('mousemove', event => {
      if (this.lightboxZoom <= 1) return;

      const rect = this.lightboxStage.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      this.lightboxImage.style.transformOrigin = `${Math.max(
        0,
        Math.min(100, x)
      )}% ${Math.max(0, Math.min(100, y))}%`;
    });

    this.lightboxStage?.addEventListener('click', event => {
      if (event.target !== this.lightboxImage) return;

      const rect = this.lightboxStage.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      if (this.lightboxZoom === 1) {
        this.setLightboxZoom(2, x, y);
      } else {
        this.setLightboxZoom(1);
      }
    });

    this.lightboxStage?.addEventListener(
      'wheel',
      event => {
        event.preventDefault();

        const rect = this.lightboxStage.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        const direction = event.deltaY < 0 ? 1 : -1;

        this.setLightboxZoom(
          this.lightboxZoom + direction * this.lightboxZoomStep,
          x,
          y
        );
      },
      { passive: false }
    );

    this.handleLightboxKeydown = event => {
      if (!this.lightbox?.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        this.closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        this.showPreviousLightboxImage();
      } else if (event.key === 'ArrowRight') {
        this.showNextLightboxImage();
      } else if (event.key === '+' || event.key === '=') {
        this.setLightboxZoom(this.lightboxZoom + this.lightboxZoomStep);
      } else if (event.key === '-') {
        this.setLightboxZoom(this.lightboxZoom - this.lightboxZoomStep);
      } else if (event.key === '0') {
        this.setLightboxZoom(1);
      }
    };

    document.addEventListener('keydown', this.handleLightboxKeydown);
  }

  openLightbox(currentSrc) {
    if (!this.lightbox) this.buildLightbox();

    this.lightboxImages = this.getGalleryImages();

    if (!this.lightboxImages.length) return;

    const normalizedCurrent = currentSrc?.split('?')[0];

    const currentIndex = this.lightboxImages.findIndex(item => {
      return item.src.split('?')[0] === normalizedCurrent;
    });

    this.lightboxIndex = currentIndex >= 0 ? currentIndex : 0;

    if (this.lightboxThumbs) {
      this.lightboxThumbs.innerHTML = '';

      this.lightboxImages.forEach((item, index) => {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'ez-lightbox__thumb';
        thumb.setAttribute('data-ez-lightbox-thumb', '');
        thumb.setAttribute('aria-label', `View image ${index + 1}`);

        const image = document.createElement('img');
        image.src = item.src;
        image.alt = '';
        image.loading = 'lazy';

        thumb.appendChild(image);

        thumb.addEventListener('click', () => {
          this.lightboxIndex = index;
          this.renderLightboxImage();
        });

        this.lightboxThumbs.appendChild(thumb);
      });
    }

    this.renderLightboxImage();

    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ez-lightbox-open');
    document.body.classList.add('ez-lightbox-open');

    this.lightbox
      .querySelector('[data-ez-lightbox-close]')
      ?.focus({ preventScroll: true });
  }

  closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('ez-lightbox-open');
    document.body.classList.remove('ez-lightbox-open');
    this.setLightboxZoom?.(1);

    this.galleryMain?.focus?.({ preventScroll: true });
  }

  bindBundles() {
    if (!this.qtyInput) return;

    this.root.querySelectorAll('[data-ez-bundle]').forEach(label => {
      label.addEventListener('click', () => {
        this.root
          .querySelectorAll('[data-ez-bundle]')
          .forEach(item => item.classList.remove('is-selected'));

        label.classList.add('is-selected');
        this.qtyInput.value = label.dataset.quantity || '1';

        this.selectedButtonText =
          label.dataset.buttonText ||
          this.button.dataset.availableText ||
          'ADD TO CART';

        if (!this.button.disabled) {
          this.button.textContent = this.selectedButtonText;
        }

        const radio = label.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
      });
    });
  }

  bindVariants() {
    this.root.querySelectorAll('[data-option-value]').forEach(button => {
      button.addEventListener('click', () => {
        const group = button.closest('[data-option-position]');
        if (!group) return;

        group
          .querySelectorAll('[data-option-value]')
          .forEach(item => item.classList.remove('is-active'));

        button.classList.add('is-active');
        this.selectedOptions[group.dataset.optionPosition] =
          button.dataset.optionValue;

        const label = group.querySelector('[data-selected-label]');
        if (label) label.textContent = button.dataset.optionValue;

        this.updateVariant();
      });
    });
  }

  updateVariant() {
    if (!this.idInput || !this.button) return;

    const values = Object.keys(this.selectedOptions)
      .sort((a, b) => Number(a) - Number(b))
      .map(position => this.selectedOptions[position]);

    const variant = this.variants.find(
      item =>
        item.options.length === values.length &&
        item.options.every((value, index) => value === values[index])
    );

    if (!variant) {
      this.button.disabled = true;
      this.button.textContent = 'UNAVAILABLE';
      return;
    }

    this.idInput.value = variant.id;

    if (this.price) {
      this.price.textContent = variant.price_formatted;
    }

    this.button.disabled = !variant.available;
    this.button.textContent = variant.available
      ? this.selectedButtonText
      : 'SOLD OUT';

    if (variant.featured_image) {
      const main = this.root.querySelector('[data-ez-main-image]');

      if (main) {
        main.src = variant.featured_image;
        main.removeAttribute('srcset');
        this.resetGalleryZoom?.();

        this.root.querySelectorAll('[data-ez-thumb]').forEach(thumb => {
          const thumbSrc = thumb.dataset.image?.split('?')[0];
          const variantSrc = variant.featured_image?.split('?')[0];

          thumb.classList.toggle(
            'is-active',
            Boolean(thumbSrc && variantSrc && thumbSrc === variantSrc)
          );
        });
      }
    }
  }
}

function initEZProductSections(scope = document) {
  const sections = [];

  if (scope.matches && scope.matches('[data-ez-product-section]')) {
    sections.push(scope);
  }

  if (scope.querySelectorAll) {
    scope
      .querySelectorAll('[data-ez-product-section]')
      .forEach(section => sections.push(section));
  }

  sections.forEach(section => new EZProductSection(section));
}

document.addEventListener('DOMContentLoaded', () =>
  initEZProductSections(document)
);

document.addEventListener('shopify:section:load', event =>
  initEZProductSections(event.target)
);
